import { Layers, Plus } from 'lucide-react';
import { useState } from 'react';
import { CreatePlanModal } from '../components/CreatePlanModal';
import { EditPlanLimitsModal } from '../components/EditPlanLimitsModal';
import { EditPlanPricingModal } from '../components/EditPlanPricingModal';
import { PlanDetailModal } from '../components/PlanDetailModal';
import { PlansTable } from '../components/PlansTable';
import { TogglePlanModal } from '../components/TogglePlanModal';
import { usePlans } from '../hooks/usePlans';
import { PlanVisibility } from '../types/plans.types';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { SectionHeader } from '@/shared/components/ui/header';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';

export default function PlansPage() {
  const { plans, isLoading, isError } = usePlans();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string>('');
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [pricingPlanId, setPricingPlanId] = useState<string>('');
  const [toggleModalOpen, setToggleModalOpen] = useState(false);
  const [togglingPlanId, setTogglingPlanId] = useState<string>('');

  const togglingPlan = plans.find((p) => p.id === togglingPlanId);

  // Planes publicos activos sin contar el que se esta cambiando: es lo que decide si
  // activarlo dejaria el onboarding con un cuarto.
  const activePublicCount = plans.filter(
    (p) => p.isActive && p.visibility === PlanVisibility.Public && p.id !== togglingPlanId,
  ).length;

  const handleViewDetail = (planId: string) => {
    setSelectedPlanId(planId);
    setDetailModalOpen(true);
  };

  const handleEdit = (planId: string) => {
    setEditingPlanId(planId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingPlanId('');
  };

  const handleEditPricing = (planId: string) => {
    setPricingPlanId(planId);
    setPricingModalOpen(true);
  };

  const handleClosePricingModal = () => {
    setPricingModalOpen(false);
    setPricingPlanId('');
  };

  const handleToggle = (planId: string) => {
    setTogglingPlanId(planId);
    setToggleModalOpen(true);
  };

  const handleCloseToggleModal = () => {
    setToggleModalOpen(false);
    setTogglingPlanId('');
  };

  return (
    <div data-testid="plans-page" className="flex flex-col gap-6 p-6">
      <SectionHeader
        sectionIcon={<Layers />}
        title="Planes"
        subtitle="Catálogo de planes de suscripción"
      >
        <Protected permissions={PERMISSIONS.PLANS_MANAGE}>
          <UnifiedButton
            data-testid="create-plan-btn"
            icon={<Plus />}
            onClick={() => setCreateModalOpen(true)}
          >
            Crear plan
          </UnifiedButton>
        </Protected>
      </SectionHeader>

      {isLoading && <div data-testid="plans-loading" className="py-8" aria-hidden />}

      {!isLoading && isError && (
        <div
          data-testid="plans-error-message"
          className="flex flex-col items-center justify-center py-12 rounded-xl bg-background border-outline"
        >
          <p className="text-negative">No se pudo cargar el catálogo. Vuelve a intentarlo.</p>
        </div>
      )}

      {!isLoading && !isError && (
        <PlansTable
          data={plans}
          onViewDetail={handleViewDetail}
          onEdit={handleEdit}
          onEditPricing={handleEditPricing}
          onToggle={handleToggle}
        />
      )}

      <CreatePlanModal open={createModalOpen} onOpenChange={setCreateModalOpen} />

      <EditPlanLimitsModal
        planId={editingPlanId}
        open={editModalOpen}
        onOpenChange={handleCloseEditModal}
      />

      <EditPlanPricingModal
        planId={pricingPlanId}
        open={pricingModalOpen}
        onOpenChange={handleClosePricingModal}
      />

      <TogglePlanModal
        planId={togglingPlanId}
        planName={togglingPlan?.name ?? ''}
        currentStatus={togglingPlan?.isActive ?? false}
        visibility={togglingPlan?.visibility ?? PlanVisibility.Public}
        activePublicCount={activePublicCount}
        open={toggleModalOpen}
        onClose={handleCloseToggleModal}
      />

      <PlanDetailModal
        planId={selectedPlanId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />
    </div>
  );
}
